import React, { useState, useMemo } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import {
  Workflow,
  Link2,
  Sparkles,
} from "lucide-react";

import {
  TechLabel,
  StatusBadge,
  CornerMarkers,
  PageContainer,
  motionFadeUp,
  C,
} from "../../design-system";
import { createPageUrl } from "../../utils";

const integrations = [
  { name: "Zapier",         category: "Automation",     desc: "5,000+ apps, ready-made recipes",       accent: C.coral },
  { name: "Make.com",       category: "Automation",     desc: "Visual workflows for complex paths",    accent: C.gold  },
  { name: "UiPath",         category: "RPA",            desc: "Enterprise-grade process automation",   accent: C.coral },
  { name: "LinkedIn",       category: "Job Boards",     desc: "Post jobs and source candidates",       accent: C.product.jobs },
  { name: "Indeed",         category: "Job Boards",     desc: "Reach millions of job seekers",         accent: C.product.recruit },
  { name: "Greenhouse",     category: "ATS",            desc: "Two-way candidate data sync",           accent: C.mint  },
  { name: "Google Calendar",category: "Productivity",   desc: "Auto-schedule interviews",              accent: C.mint  },
  { name: "Slack",          category: "Communication",  desc: "Real-time pipeline alerts",             accent: C.gold  },
  { name: "Microsoft Teams",category: "Communication",  desc: "Notify and collaborate inline",         accent: C.product.recruit },
  { name: "Zoom",           category: "Video",          desc: "Video interview integration",           accent: C.product.jobs },
  { name: "Gmail",          category: "Email",          desc: "Inbox tracking + templated nudges",     accent: C.coral },
  { name: "DocuSign",       category: "Documents",      desc: "E-signature for offer letters",         accent: C.gold  },
];

const useCases = [
  {title: "Auto-post jobs",        desc: "Every new role lands on LinkedIn, Indeed and 50+ boards in one click." },
  {title: "Interview scheduling",  desc: "Sync Google Calendar, send Zoom links, handle reminders automatically." },
  {title: "Slack notifications",    desc: "Real-time pings when candidates apply, advance or drop off." },
  {title: "Email campaigns",       desc: "Trigger personalised email sequences from any pipeline stage." },
];

const categories = ["All", ...Array.from(new Set(integrations.map((i) => i.category)))];

export default function Integrations() {
  const [selected, setSelected] = useState("All");

  const filtered = useMemo(
    () => (selected === "All" ? integrations : integrations.filter((i) => i.category === selected)),
    [selected]
  );

  return (
    <div
      className="relative"
      style={{
        background: C.paper,
        color: C.forest,
        fontFamily: "'Plus Jakarta Sans', system-ui, -apple-system, sans-serif",
      }}
    >
      {/* HERO */}
      <section className="relative pt-16 pb-20 md:pt-24 md:pb-28 border-b ds-bg-spotlight" style={{ borderColor: C.hairline }}>
        <PageContainer>
          <motion.div initial="hidden" animate="show" variants={motionFadeUp}>
            <motion.div variants={motionFadeUp} custom={0}>
              <StatusBadge>Integrations · {integrations.length} apps</StatusBadge>
            </motion.div>
            <motion.h1
              variants={motionFadeUp}
              custom={1}
              className="mt-5 text-5xl sm:text-6xl lg:text-[5.25rem] xl:text-[6rem] font-extrabold leading-[0.92] tracking-tight max-w-5xl"
              style={{ color: C.forest }}
            >
              Plays nicely
              <br />
              <span style={{ color: `${C.forest}80` }}>with your stack.</span>
            </motion.h1>
            <motion.p
              variants={motionFadeUp}
              custom={2}
              className="mt-6 text-lg md:text-xl leading-relaxed max-w-3xl"
              style={{ color: C.grid }}
            >
              Connect WebIdeal to the tools you already use boards, calendars,
              email, automation. Bring your own stack, we'll meet it where it is.
            </motion.p>
          </motion.div>
        </PageContainer>
      </section>

      {/* INTEGRATIONS GRID */}
      <section className="py-20 md:py-24 border-b" style={{ borderColor: C.hairline }}>
        <PageContainer>
          <div className="mb-10 flex items-end justify-between flex-wrap gap-4">
            <div className="max-w-2xl">
              <StatusBadge>Connected apps</StatusBadge>
              <h2 className="mt-4 text-3xl md:text-4xl xl:text-5xl font-bold leading-[1.05] tracking-tight" style={{ color: C.forest }}>
                Browse by category.
              </h2>
            </div>
            <TechLabel style={{ color: `${C.grid}99` }}>
              {filtered.length} of {integrations.length} shown
            </TechLabel>
          </div>

          {/* category tabs */}
          <div className="flex flex-wrap items-center gap-px mb-10" style={{ background: C.hairline }}>
            {categories.map((cat) => {
              const active = cat === selected;
              return (
                <button
                  key={cat}
                  type="button"
                  onClick={() => setSelected(cat)}
                  className="cursor-pointer px-4 md:px-5 py-3 text-xs font-bold uppercase transition-colors duration-150 inline-flex items-center gap-2"
                  style={{
                    letterSpacing: "0.18em",
                    background: active ? C.forest : C.paper,
                    color: active ? C.paper : C.grid,
                  }}
                >
                  <span className="w-1.5 h-1.5" style={{ background: active ? C.mint : `${C.grid}77` }} />
                  {cat}
                </button>
              );
            })}
          </div>

          {/* integrations grid */}
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-px" style={{ background: C.hairline }}>
            {filtered.map((i, idx) => (
              <motion.div
                key={i.name}
                initial={{ opacity: 0, y: 14 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, amount: 0.15 }}
                transition={{ duration: 0.35, delay: idx * 0.03 }}
                className="ds-spatial relative p-6 md:p-7 transition-colors duration-200 hover:bg-white"
                style={{ background: C.paper, borderLeft: `4px solid ${i.accent}` }}
              >
                <div className="flex items-start justify-between mb-4">
                  <div
                    className="w-12 h-12 flex items-center justify-center text-base font-extrabold tracking-tight"
                    style={{
                      background: C.forest,
                      color: C.paper,
                      letterSpacing: "0",
                    }}
                  >
                    {i.name.charAt(0)}
                  </div>
                  <TechLabel style={{ color: i.accent }}>{i.category}</TechLabel>
                </div>
                <h3 className="text-base font-bold leading-tight" style={{ color: C.forest }}>{i.name}</h3>
                <p className="mt-1.5 text-xs leading-relaxed" style={{ color: C.grid }}>{i.desc}</p>
                <div className="mt-4 flex items-center gap-1.5">
                  <span className="w-1.5 h-1.5 rounded-full animate-pulse" style={{ background: i.accent }} />
                  <TechLabel style={{ color: `${C.grid}99` }}>connected</TechLabel>
                </div>
              </motion.div>
            ))}
          </div>
        </PageContainer>
      </section>

      {/* USE CASES */}
      <section className="py-20 md:py-24 border-b" style={{ borderColor: C.hairline }}>
        <PageContainer>
          <div className="mb-12 max-w-2xl">
            <StatusBadge>What people automate</StatusBadge>
            <h2 className="mt-4 text-4xl md:text-5xl font-bold leading-[1.05] tracking-tight" style={{ color: C.forest }}>
              Common automations.
            </h2>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-px" style={{ background: C.hairline }}>
            {useCases.map((u) => (
              <div key={u.idx} className="p-7 md:p-8" style={{ background: C.paper }}>
                <div className="flex items-center justify-between mb-4">
                  <div className="w-11 h-11 flex items-center justify-center" style={{ background: C.forest, color: C.paper }}>
                    <Workflow className="w-5 h-5" strokeWidth={1.6} />
                  </div>
                  <TechLabel style={{ color: `${C.grid}99` }}>{u.idx}</TechLabel>
                </div>
                <h3 className="text-lg font-bold leading-tight" style={{ color: C.forest }}>{u.title}</h3>
                <p className="mt-2 text-sm leading-relaxed" style={{ color: C.grid }}>{u.desc}</p>
              </div>
            ))}
          </div>
        </PageContainer>
      </section>

      {/* CTA */}
      <section className="py-20 md:py-24" style={{ background: C.forest, color: C.paper }}>
        <PageContainer>
          <div className="relative border p-10 md:p-14" style={{ borderColor: "rgba(158,255,191,0.25)" }}>
            <CornerMarkers color={C.mint} />
            <div className="grid md:grid-cols-12 gap-8 items-center">
              <div className="md:col-span-7">
                <StatusBadge color={C.mint}>Need a custom integration?</StatusBadge>
                <h2 className="mt-4 text-3xl md:text-4xl xl:text-5xl font-bold leading-[1.05] tracking-tight">
                  Don't see your tool?
                  <br />
                  <span style={{ color: C.mint }}>We can build it.</span>
                </h2>
                <p className="mt-4 text-base md:text-lg max-w-xl" style={{ color: "rgba(247,247,245,0.75)" }}>
                  Most custom integrations ship in two weeks. Open APIs and webhooks
                  let you wire up anything we haven't already.
                </p>
              </div>
              <div className="md:col-span-5 flex flex-wrap gap-3 md:justify-end">
                <Link to={createPageUrl("Contact")}>
                  <button type="button" className="ds-skeu-btn-mint cursor-pointer h-12 px-7 text-sm font-semibold inline-flex items-center">
                    <Sparkles className="w-4 h-4 mr-2" />
                    Request integration
                  </button>
                </Link>
                <a
                  href="https://github.com/webidealsoftwaresolutions-web"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="cursor-pointer h-12 px-6 text-sm font-semibold border bg-transparent inline-flex items-center hover:bg-[#9EFFBF] hover:text-[#1A3C2B] transition-colors duration-150"
                  style={{ borderColor: "rgba(158,255,191,0.4)", color: C.paper }}
                >
                  <Link2 className="w-4 h-4 mr-2" />
                  API docs
                </a>
              </div>
            </div>
          </div>
        </PageContainer>
      </section>
    </div>
  );
}
